import { Component } from '@theme/component';

/**
 * Back in Stock notification component
 * Integrates with Klaviyo API to send back in stock notifications
 * 
 * @extends Component
 */
export default class BackInStock extends Component {
  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {string} */
  klaviyoApiKey = 'Q5hKSF';

  connectedCallback() {
    super.connectedCallback();
    this.createDialog();
    this.attachEventListeners();
  }

  /**
   * Creates the modal dialog structure
   */
  createDialog() {
    const dialog = document.createElement('dialog');
    dialog.className = 'back-in-stock-dialog';
    dialog.setAttribute('aria-labelledby', 'bis-title');
    
    const productTitle = this.dataset.productTitle || 'this product';
    const productId = this.dataset.productId || '';
    const productVariantId = this.dataset.variantId || '';
    const productUrl = this.dataset.productUrl || '';
    const productImage = this.dataset.productImage || '';

    dialog.innerHTML = `
      <div class="back-in-stock-dialog__content">
        <button type="button" class="back-in-stock-dialog__close" aria-label="Close dialog">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="4" y1="4" x2="16" y2="16"/>
            <line x1="16" y1="4" x2="4" y2="16"/>
          </svg>
        </button>
        
        <h2 id="bis-title" class="back-in-stock-dialog__title">Notify me when available</h2>
        <p class="back-in-stock-dialog__description">
          Enter your details below and we'll let you know when <strong>${this.escapeHtml(productTitle)}</strong> is back in stock.
        </p>

        <form class="back-in-stock-form" data-product-id="${productId}" data-variant-id="${productVariantId}" data-product-url="${productUrl}" data-product-image="${productImage}">
          <div class="back-in-stock-form__contact-type">
            <label class="back-in-stock-radio">
              <input type="radio" name="contact-type" value="email" checked>
              <span>Email</span>
            </label>
            <label class="back-in-stock-radio">
              <input type="radio" name="contact-type" value="phone">
              <span>SMS</span>
            </label>
          </div>

          <div class="back-in-stock-form__input-group back-in-stock-form__email-group">
            <label for="bis-email" class="back-in-stock-form__label">Email address</label>
            <input 
              type="email" 
              id="bis-email" 
              name="email" 
              class="back-in-stock-form__input"
              placeholder="your@email.com"
              required
            >
          </div>

          <div class="back-in-stock-form__input-group back-in-stock-form__phone-group" style="display: none;">
            <label for="bis-phone" class="back-in-stock-form__label">Phone number</label>
            <input 
              type="tel" 
              id="bis-phone" 
              name="phone" 
              class="back-in-stock-form__input"
              placeholder="+44 7XXX XXXXXX"
            >
          </div>

          <div class="back-in-stock-form__consent">
            <label class="back-in-stock-checkbox">
              <input type="checkbox" name="consent" required>
              <span>I agree to receive back in stock notifications and marketing communications</span>
            </label>
          </div>

          <div class="back-in-stock-form__error" style="display: none;"></div>

          <button type="submit" class="button back-in-stock-form__submit">
            Notify me
          </button>
        </form>

        <div class="back-in-stock-success" style="display: none;">
          <svg class="back-in-stock-success__icon" width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="3"/>
            <path d="M20 32L28 40L44 24" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h3 class="back-in-stock-success__title">You're on the list!</h3>
          <p class="back-in-stock-success__message">We'll notify you as soon as this item is back in stock.</p>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);
    this.dialog = dialog;
    this.form = dialog.querySelector('.back-in-stock-form');
  }

  /**
   * Attaches event listeners
   */
  attachEventListeners() {
    if (!this.dialog || !this.form) return;

    // Open dialog button
    this.addEventListener('click', () => this.openDialog());

    // Close dialog button
    const closeBtn = this.dialog.querySelector('.back-in-stock-dialog__close');
    closeBtn?.addEventListener('click', () => this.closeDialog());

    // Close on backdrop click
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) {
        this.closeDialog();
      }
    });

    // Close on escape key
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDialog();
      }
    });

    // Contact type toggle
    const radioButtons = this.form.querySelectorAll('input[name="contact-type"]');
    radioButtons.forEach(radio => {
      radio.addEventListener('change', () => this.toggleContactType());
    });

    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  /**
   * Opens the dialog
   */
  openDialog() {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    
    // Reset form
    this.form?.reset();
    this.hideError();
    this.hideSuccess();
    this.toggleContactType();
  }

  /**
   * Closes the dialog
   */
  closeDialog() {
    if (!this.dialog) return;
    this.dialog.close();
    document.body.style.overflow = '';
  }

  /**
   * Toggles between email and phone input
   */
  toggleContactType() {
    if (!this.form) return;

    const contactType = this.form.querySelector('input[name="contact-type"]:checked')?.value;
    const emailGroup = this.form.querySelector('.back-in-stock-form__email-group');
    const phoneGroup = this.form.querySelector('.back-in-stock-form__phone-group');
    const emailInput = this.form.querySelector('#bis-email');
    const phoneInput = this.form.querySelector('#bis-phone');

    if (contactType === 'email') {
      emailGroup.style.display = 'block';
      phoneGroup.style.display = 'none';
      emailInput.required = true;
      phoneInput.required = false;
    } else {
      emailGroup.style.display = 'none';
      phoneGroup.style.display = 'block';
      emailInput.required = false;
      phoneInput.required = true;
    }
  }

  /**
   * Handles form submission
   */
  async handleSubmit() {
    if (!this.form) return;

    const formData = new FormData(this.form);
    const contactType = formData.get('contact-type');
    const email = formData.get('email');
    const phone = formData.get('phone');
    const consent = formData.get('consent');

    // Validate consent
    if (!consent) {
      this.showError('Please agree to receive notifications');
      return;
    }

    // Get product data
    const productId = this.form.dataset.productId;
    const variantId = this.form.dataset.variantId;
    const productUrl = this.form.dataset.productUrl;
    const productImage = this.form.dataset.productImage;
    const productTitle = this.dataset.productTitle || '';

    // Show loading state
    const submitBtn = this.form.querySelector('.back-in-stock-form__submit');
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Sending...';
    submitBtn.disabled = true;

    try {
      // Subscribe to Klaviyo back in stock
      const success = await this.subscribeToKlaviyo({
        email: contactType === 'email' ? email : null,
        phone: contactType === 'phone' ? phone : null,
        productId,
        variantId,
        productUrl: window.location.origin + productUrl,
        productImage: productImage ? window.location.origin + productImage : null,
        productTitle,
      });

      if (success) {
        this.showSuccess();
        // Auto-close after 2.5 seconds
        setTimeout(() => {
          this.closeDialog();
        }, 2500);
      } else {
        this.showError('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Back in stock error:', error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }
  }

  /**
   * Subscribes to Klaviyo back in stock notifications
   * @param {Object} data - Subscription data
   * @returns {Promise<boolean>}
   */
  async subscribeToKlaviyo(data) {
    const { email, phone, productId, variantId, productUrl, productImage, productTitle } = data;

    // Build profile attributes
    const profileAttributes = {};
    if (email) profileAttributes.email = email;
    if (phone) profileAttributes.phone_number = phone;

    // Prepare the payload for Klaviyo - profile needs 'data' wrapper
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          },
          channels: email ? ['EMAIL'] : ['SMS'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    console.log('Klaviyo BIS Payload:', JSON.stringify(payload, null, 2));

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoApiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-07-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok || response.status === 202) {
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Klaviyo API error:', JSON.stringify(errorData, null, 2));
        console.error('Response status:', response.status);
        console.error('Sent payload:', JSON.stringify(payload, null, 2));
        if (errorData.errors && errorData.errors.length > 0) {
          console.error('Error details:', errorData.errors[0]);
        }
        return false;
      }
    } catch (error) {
      console.error('Network error:', error);
      return false;
    }
  }

  /**
   * Shows error message
   * @param {string} message
   */
  showError(message) {
    if (!this.form) return;
    const errorEl = this.form.querySelector('.back-in-stock-form__error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
  }

  /**
   * Hides error message
   */
  hideError() {
    if (!this.form) return;
    const errorEl = this.form.querySelector('.back-in-stock-form__error');
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  /**
   * Shows success message
   */
  showSuccess() {
    if (!this.dialog) return;
    const form = this.dialog.querySelector('.back-in-stock-form');
    const success = this.dialog.querySelector('.back-in-stock-success');
    if (form) form.style.display = 'none';
    if (success) success.style.display = 'block';
  }

  /**
   * Hides success message
   */
  hideSuccess() {
    if (!this.dialog) return;
    const form = this.dialog.querySelector('.back-in-stock-form');
    const success = this.dialog.querySelector('.back-in-stock-success');
    if (form) form.style.display = 'block';
    if (success) success.style.display = 'none';
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up dialog
    if (this.dialog && this.dialog.parentNode) {
      this.dialog.parentNode.removeChild(this.dialog);
    }
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}
